"use client";

import clsx from "clsx";
import Link from "next/link";
import {memo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {TokenBreakdown} from "@/utils/chat/tokenEstimation";

interface ContextWheelProps {
   percent: number;
   tokenLimitReached: boolean;
   tokenBreakdown?: TokenBreakdown;
   /** URL for the "Start new chat" link. Defaults to "/gailgpt". */
   newChatHref?: string;
}

export type Severity = "normal" | "warning" | "critical";

/** The context wheel (and its "Start new chat" link) become visible at this threshold. */
export const CONTEXT_WHEEL_VISIBILITY_THRESHOLD = 70;

export function getSeverity(percent: number, tokenLimitReached: boolean): Severity {
   if (tokenLimitReached || percent >= 95) return "critical";
   if (percent >= 85) return "warning";
   return "normal";
}

const numberFormatters = new Map<string, Intl.NumberFormat>();
function getCompactFormatter(locale?: string): Intl.NumberFormat {
   const key = locale ?? "";
   let fmt = numberFormatters.get(key);
   if (!fmt) {
      fmt = new Intl.NumberFormat(locale, {notation: "compact", maximumFractionDigits: 1});
      numberFormatters.set(key, fmt);
   }
   return fmt;
}

export function formatTokenCount(tokens: number, locale?: string): string {
   return getCompactFormatter(locale).format(tokens);
}

interface SeverityStyles {
   barColor: string;
   pillBg: string;
   pillTextColor: string;
   badgeBg: string;
   progressBg: string;
   warningTextColor: string;
}

function getSeverityStyles(severity: Severity): SeverityStyles {
   switch (severity) {
      case "critical":
         return {
            barColor: "#EF4444",
            pillBg: "bg-red-50 hover:bg-red-100",
            pillTextColor: "text-red-600",
            badgeBg: "bg-red-100 text-red-600",
            progressBg: "#EF4444",
            warningTextColor: "text-red-500",
         };
      case "warning":
         return {
            barColor: "#F59E0B",
            pillBg: "bg-amber-50 hover:bg-amber-100",
            pillTextColor: "text-amber-700",
            badgeBg: "bg-amber-100 text-amber-700",
            progressBg: "#F59E0B",
            warningTextColor: "text-amber-600",
         };
      default:
         return {
            barColor: "#94A3B8",
            pillBg: "bg-100 hover:bg-200",
            pillTextColor: "text-400",
            badgeBg: "bg-sky-100 text-sky-700",
            progressBg: "#3B82F6",
            warningTextColor: "",
         };
   }
}

function buildBreakdownRows(
   tokenBreakdown: TokenBreakdown | undefined,
   t: (key: string) => string
) {
   if (!tokenBreakdown) return null;

   const rows = [
      {
         label: t("tokenBreakdownConversation"),
         tokens: tokenBreakdown.conversation,
         color: "#3B82F6",
      },
   ];

   if (tokenBreakdown.system > 0) {
      rows.push({
         label: t("tokenBreakdownSystem"),
         tokens: tokenBreakdown.system,
         color: "#8B5CF6",
      });
   }

   if (tokenBreakdown.attachments > 0) {
      rows.push({
         label: t("tokenBreakdownAttachments"),
         tokens: tokenBreakdown.attachments,
         color: "#F59E0B",
      });
   }

   return rows;
}

/** Inline SVG mini gauge for the pill trigger — 18×18 */
const MiniGauge: React.FC<{percent: number; color: string}> = ({percent, color}) => {
   const r = 14;
   const c = 2 * Math.PI * r;
   const dash = (percent / 100) * (c * 0.88); // 0.88 maps to ~315° arc
   return (
      <svg width="18" height="18" viewBox="0 0 36 36" className="shrink-0">
         <circle cx="18" cy="18" r={r} fill="none" stroke="#E5E7EB" strokeWidth="3.5" />
         <circle
            cx="18"
            cy="18"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="3.5"
            strokeDasharray={`${dash} ${c}`}
            strokeLinecap="round"
            transform="rotate(-90 18 18)"
            style={{transition: "stroke-dasharray 0.5s ease, stroke 0.3s"}}
         />
      </svg>
   );
};

const ContextWheel: React.FC<ContextWheelProps> = memo(({
   percent,
   tokenLimitReached,
   tokenBreakdown,
   newChatHref = "/gailgpt",
}) => {
   const {t, i18n} = useTranslation("ask");
   const [open, setOpen] = useState(false);
   const severity = getSeverity(percent, tokenLimitReached);

   const {barColor, pillBg, pillTextColor} = getSeverityStyles(severity);

   let triggerLabel = `${percent}%`;
   if (tokenLimitReached) triggerLabel = t("limitReached");
   else if (severity === "critical") triggerLabel = t("nearLimit");

   // Defer heavy popover content computation until the popover is actually open.
   const renderPopoverContent = () => {
      const locale = i18n.language;
      const isElevated = severity !== "normal";
      const {badgeBg, progressBg, warningTextColor} = getSeverityStyles(severity);
      const breakdownRows = buildBreakdownRows(tokenBreakdown, t);
      const totalTokens = tokenBreakdown
         ? tokenBreakdown.conversation + tokenBreakdown.system + tokenBreakdown.attachments
         : 0;

      return (
         <>
            <div className="p-5 pb-4">
               <div className="flex items-center justify-between mb-1">
                  <span className="text-[15px] font-semibold text-900">
                     {t("tokenUsageLabel")}
                  </span>
                  <span
                     className={clsx(
                        "text-xs font-semibold px-2.5 py-0.5 rounded-full tabular-nums",
                        badgeBg
                     )}
                  >
                     {percent}%
                  </span>
               </div>

               {totalTokens > 0 && (
                  <p className="text-xs text-gray-400 mb-3 tabular-nums">
                     ~{formatTokenCount(totalTokens, locale)} {t("tokenBreakdownTokensUsed")}
                  </p>
               )}

               <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                     className="h-full rounded-full transition-all duration-500 ease-out"
                     style={{width: `${percent}%`, background: progressBg}}
                  />
               </div>

               {isElevated && (
                  <p className={clsx("text-xs font-medium mt-3", warningTextColor)}>
                     {t("tokenWarningMessage")}
                  </p>
               )}

               {breakdownRows && breakdownRows.length > 0 && (
                  <div className="flex flex-col gap-2 mt-4">
                     {breakdownRows.map((row) => (
                        <div key={row.label} className="flex items-center justify-between">
                           <div className="flex items-center gap-2.5">
                              <div
                                 className="w-2 h-2 rounded-full shrink-0"
                                 style={{background: row.color}}
                              />
                              <span className="text-sm text-gray-600">{row.label}</span>
                           </div>
                           <span className="text-sm text-gray-400 tabular-nums">
                              {formatTokenCount(row.tokens, locale)}
                           </span>
                        </div>
                     ))}
                  </div>
               )}
            </div>

            {percent >= CONTEXT_WHEEL_VISIBILITY_THRESHOLD && (
               <div className="border-t border-gray-100 px-5 py-3">
                  <Link
                     href={newChatHref}
                     className="block text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                     {t("startNewChat")}
                  </Link>
               </div>
            )}
         </>
      );
   };

   return (
      <Popover open={open} onOpenChange={setOpen}>
         <PopoverTrigger asChild>
            <button
               type="button"
               aria-label={t("contextWheelAriaLabel", {percent})}
               className={clsx(
                  "inline-flex items-center gap-2 shrink-0 rounded-lg px-3 py-2",
                  "transition-all duration-150 active:scale-[0.97]",
                  "focus:outline-none focus:ring-0",
                  pillBg
               )}
            >
               <MiniGauge percent={percent} color={barColor} />
               <span className={clsx("text-sm font-medium whitespace-nowrap", pillTextColor)}>
                  {triggerLabel}
               </span>
            </button>
         </PopoverTrigger>

         <PopoverContent
            side="top"
            align="end"
            sideOffset={8}
            className={clsx(
               "w-[300px] p-0 overflow-hidden rounded-2xl",
               "transition-[filter] duration-200",
               "data-[state=open]:blur-0 data-[state=closed]:blur-[2px]"
            )}
            style={{
               animationDuration: "180ms",
               animationTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
               "--tw-enter-scale": "0.96",
               "--tw-exit-scale": "0.96",
            } as React.CSSProperties}
            portalToBody
         >
            {open && renderPopoverContent()}
         </PopoverContent>
      </Popover>
   );
});

ContextWheel.displayName = "ContextWheel";

export default ContextWheel;
